'use client';

import { useWindowDimensions } from '@repo/design-system/hooks/useWindowDimensions';
import { css } from '@repo/panda/css';
import type React from 'react';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findClosestSnapIndex } from './mobile-drawer-utils';
import { useDrawerDrag } from './useDrawerDrag';
const MIN_HEIGHT = 100;
const DRAG_HANDLE_HEIGHT = 20;
const CONTENT_BOTTOM_PADDING = 24;
interface MobileDrawerProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  minHeight?: number;
  maxHeight?: number;
  snapThreshold?: number;
  swipeThreshold?: number;
  className?: string;
  overlayClassName?: string;
  draggableAreaClassName?: string;
  indicatorClassName?: string;
  contentClassName?: string;
  showOverlay?: boolean;
  overlayOpacity?: number;
  borderRadius?: string;
  backgroundColor?: string;
  boxShadow?: string;
  zIndex?: number;
  margin?: string;
  maxWidth?: string;
  onOverlayClick?: () => void;
  style?: CSSProperties;
  forceExpanded?: boolean;
  snapPoints?: number[];
  initialSnapIndex?: number;
  onSnapChange?: (index: number) => void;
  /** When true, the drawer height is clamped to its content size. */
  fitContent?: boolean;
  /** When this key changes, the drawer re-measures and re-fits its content height. */
  contentKey?: string;
  /** Pixels reserved at the top of the viewport the drawer will never cover. */
  topReserve?: number;
  /** Looser top reserve for drag cap (so a drag can reach higher than snap rest). Defaults to `topReserve`. */
  dragTopReserve?: number;
}
const drawerContainerStyles = css({
  position: 'fixed',
  bottom: 'none',
  left: 'none',
  right: 'none',
  zIndex: '[1000]',
  pointerEvents: 'none'
});
const drawerStyles = css({
  position: 'relative',
  width: 'full',
  maxWidth: '[460px]',
  backgroundColor: 'wemod.elevated.100',
  borderTopLeftRadius: '32',
  borderTopRightRadius: '32',
  boxShadow: 'lg',
  pointerEvents: 'auto',
  transition: 'all',
  transitionDuration: 'fast',
  overflow: 'hidden',
  marginLeft: 'auto',
  marginRight: 'auto',
  marginBottom: 'none',
  willChange: '[transform, height]',
  contain: '[layout style]'
});
const overlayStyles = (isOpen: boolean, opacity: CSSProperties['opacity'] = 1) => css({
  position: 'fixed',
  top: 'none',
  left: 'none',
  right: 'none',
  bottom: 'none',
  backgroundColor: 'blackAlpha.50',
  zIndex: '[999]',
  opacity: isOpen ? opacity : 0,
  visibility: isOpen ? 'visible' : 'hidden',
  transition: 'all',
  transitionDuration: 'fast',
  pointerEvents: isOpen ? 'auto' : 'none'
});
const draggableTopAreaStyles = css({
  position: 'absolute',
  top: 'none',
  left: 'none',
  right: 'none',
  height: '[20px]',
  zIndex: '[1001]',
  cursor: 'grab',
  touchAction: 'none',
  _active: {
    cursor: 'grabbing'
  }
});
const swipeIndicatorStyles = css({
  position: 'absolute',
  top: '8',
  width: '[48px]',
  height: '[5px]',
  backgroundColor: 'whiteAlpha.30',
  borderRadius: 'full',
  zIndex: '[1001]',
  cursor: 'grab',
  _active: {
    cursor: 'grabbing'
  }
});
const contentStyles = css({
  height: 'full',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
});
const DEFAULT_SNAP_POINTS = [0, 0.45, 0.9];

/**
 * A mobile-friendly bottom drawer with snap-point support.
 *
 * The drawer defines a set of fractional `snapPoints` (default: [0, 0.45, 0.9]) that map to
 * heights between `minHeight` and the viewport height. Users can swipe/drag the drawer between
 * snap points; a velocity-based algorithm determines the target snap on release. Snapping to
 * index 0 closes the drawer. An overlay tap steps the drawer down by one snap point.
 *
 * State is split between React state (for re-renders) and refs (for smooth drag animation).
 * During a drag, the drawer height is written directly to the DOM via ref to avoid per-frame
 * React re-renders; React state is synced once on drag end before snapping.
 */
export const MobileDrawer = ({
  children,
  isOpen: controlledIsOpen,
  onOpenChange,
  minHeight = MIN_HEIGHT,
  maxHeight = Number.POSITIVE_INFINITY,
  // This will be updated by useWindowDimensions
  swipeThreshold = 0.1,
  className,
  overlayClassName,
  draggableAreaClassName,
  indicatorClassName,
  contentClassName,
  showOverlay = true,
  overlayOpacity = 0.5,
  borderRadius,
  backgroundColor,
  boxShadow,
  zIndex = 1000,
  margin,
  maxWidth,
  onOverlayClick,
  style,
  forceExpanded = false,
  snapPoints = DEFAULT_SNAP_POINTS,
  initialSnapIndex = 1,
  onSnapChange,
  fitContent = false,
  contentKey,
  topReserve = 0,
  dragTopReserve
}: MobileDrawerProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [height, setHeightState] = useState(minHeight);
  const drawerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef(minHeight);
  const prevIsOpenRef = useRef(false);
  const prevForceExpandedRef = useRef(forceExpanded);
  const {
    height: windowHeight
  } = useWindowDimensions();
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = useCallback((open: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  }, [controlledIsOpen, onOpenChange]);
  const snapScaleMax = Math.min(maxHeight, windowHeight);
  const currentMaxHeight = Math.max(minHeight, snapScaleMax - topReserve);
  const dragReserve = dragTopReserve ?? topReserve;
  const currentDragMaxHeight = Math.max(currentMaxHeight, snapScaleMax - dragReserve);
  const isDraggingRef = useRef(false);
  const isTouchActiveRef = useRef(false);

  // Keep heightRef in sync when setHeight is called outside dragging.
  const setHeight = useCallback((h: number) => {
    heightRef.current = h;
    setHeightState(h);
  }, []);
  const contentCeilingRef = useRef<number>(Number.POSITIVE_INFINITY);
  const measureContentHeight = useCallback(() => {
    const el = contentRef.current;
    if (!el) return Number.POSITIVE_INFINITY;
    const prevHeight = el.style.height;
    const prevOverflow = el.style.overflow;
    el.style.height = 'auto';
    el.style.overflow = 'visible';
    const measured = el.scrollHeight + DRAG_HANDLE_HEIGHT + CONTENT_BOTTOM_PADDING;
    el.style.height = prevHeight;
    el.style.overflow = prevOverflow;
    contentCeilingRef.current = measured;
    return measured;
  }, []);

  // Snap fractions scale off the full viewport so the non-max snap doesn't shrink when
  // `topReserve` is in play; each snap is then clamped at `currentMaxHeight` so the
  // user-configured reserve still governs the maximum.
  const pixelSnapHeights = useMemo(() => snapPoints.map(p => Math.min(minHeight + p * (snapScaleMax - minHeight), currentMaxHeight)), [snapPoints, minHeight, snapScaleMax, currentMaxHeight]);

  // When fitContent is active, cap snap heights and drag max to the measured content height
  const getEffectiveSnapHeights = useCallback(() => {
    if (!fitContent || contentCeilingRef.current === Number.POSITIVE_INFINITY) return pixelSnapHeights;
    const ceiling = Math.min(contentCeilingRef.current, currentMaxHeight);
    return pixelSnapHeights.map(h_0 => Math.min(h_0, ceiling));
  }, [fitContent, pixelSnapHeights, currentMaxHeight]);
  const getEffectiveMaxHeight = useCallback(() => {
    if (!fitContent || contentCeilingRef.current === Number.POSITIVE_INFINITY) return currentDragMaxHeight;
    return Math.min(contentCeilingRef.current, currentDragMaxHeight);
  }, [fitContent, currentDragMaxHeight]);

  // Observes content size until stable, then disconnects so drag/snap is unaffected.
  // Skip clamping while forceExpanded is active to avoid snap-up then snap-down flicker.
  useEffect(() => {
    if (!fitContent || !contentRef.current || !isOpen || forceExpanded) return;
    const el_0 = contentRef.current;
    let lastMeasured = 0;
    let stableCount = 0;
    const reobserve = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(el_0);
      for (const child of el_0.children) {
        resizeObserver.observe(child);
      }
    };
    const clampToContent = () => {
      if (isDraggingRef.current || isTouchActiveRef.current) return;
      const measured_0 = measureContentHeight();
      if (measured_0 === lastMeasured) {
        stableCount++;
        if (stableCount >= 2) {
          resizeObserver.disconnect();
          return;
        }
      } else {
        stableCount = 0;
      }
      lastMeasured = measured_0;
      if (heightRef.current > measured_0) {
        heightRef.current = measured_0;
        if (drawerRef.current) {
          drawerRef.current.style.transition = 'none';
          drawerRef.current.style.height = `${measured_0}px`;
        }
        setHeightState(measured_0);
      }
    };
    const clampAfterNavigation = () => {
      if (isDraggingRef.current || isTouchActiveRef.current) return;
      const measured_1 = Math.min(measureContentHeight(), currentMaxHeight);
      // Resize to fit the new content (grow or shrink)
      if (heightRef.current !== measured_1) {
        heightRef.current = measured_1;
        if (drawerRef.current) {
          drawerRef.current.style.transition = 'none';
          drawerRef.current.style.height = `${measured_1}px`;
        }
        setHeightState(measured_1);
      }
    };
    const resizeObserver = new ResizeObserver(clampToContent);
    reobserve();
    const mutationObserver = new MutationObserver(() => {
      stableCount = 0;
      requestAnimationFrame(clampAfterNavigation);
    });
    mutationObserver.observe(el_0, {
      childList: true,
      subtree: true,
      characterData: true
    });
    clampToContent();
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [fitContent, isOpen, forceExpanded, measureContentHeight, currentMaxHeight]);

  // Re-measure and fit when contentKey changes (e.g. switching between pins)
  useEffect(() => {
    if (!fitContent || !isOpen || !contentKey || isDraggingRef.current) return;
    let cancelled = false;
    // Double-rAF ensures React has committed and the browser has painted
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const measured_2 = Math.min(measureContentHeight(), currentMaxHeight);
        if (Math.abs(heightRef.current - measured_2) > 1) {
          heightRef.current = measured_2;
          if (drawerRef.current) {
            drawerRef.current.style.transition = 'none';
            drawerRef.current.style.height = `${measured_2}px`;
          }
          setHeightState(measured_2);
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [contentKey, fitContent, isOpen, measureContentHeight, currentMaxHeight]);

  // Safe accessor — snapPoints always has at least the default 3 entries
  const getSnapHeight = useCallback((index: number) => pixelSnapHeights[index] ?? minHeight, [pixelSnapHeights, minHeight]);
  const snapToNearest = useCallback((currentHeight: number, velocity: number) => {
    const effectiveSnaps = getEffectiveSnapHeights();
    let targetIndex: number;
    const closestIndex = findClosestSnapIndex(currentHeight, effectiveSnaps);
    if (Math.abs(velocity) > swipeThreshold) {
      if (velocity > 0) {
        // Swiping up — go to next higher snap
        targetIndex = Math.min(closestIndex + 1, effectiveSnaps.length - 1);
        // If already at or above closest, ensure we go up
        if (currentHeight >= (effectiveSnaps[closestIndex] ?? 0) && closestIndex < effectiveSnaps.length - 1) {
          targetIndex = closestIndex + 1;
        }
      } else {
        // Swiping down — go to next lower snap
        targetIndex = Math.max(closestIndex - 1, 0);
        // If already at or below closest, ensure we go down
        if (currentHeight <= (effectiveSnaps[closestIndex] ?? 0) && closestIndex > 0) {
          targetIndex = closestIndex - 1;
        }
      }
    } else {
      targetIndex = closestIndex;
    }
    setHeight(effectiveSnaps[targetIndex] ?? minHeight);
    onSnapChange?.(targetIndex);
    if (targetIndex === 0) {
      setIsOpen(false);
    }
  }, [getEffectiveSnapHeights, swipeThreshold, setHeight, onSnapChange, setIsOpen, minHeight]);
  const getInitialSnapHeight = useCallback(() => {
    const snaps = getEffectiveSnapHeights();
    const idx = Math.min(initialSnapIndex, snaps.length - 1);
    const baseline = snaps[idx] ?? minHeight;
    if (fitContent) return baseline;
    const measured_3 = measureContentHeight();
    if (!Number.isFinite(measured_3)) return baseline;
    const max = snaps[snaps.length - 1] ?? baseline;
    return Math.min(max, Math.max(baseline, measured_3));
  }, [getEffectiveSnapHeights, initialSnapIndex, minHeight, fitContent, measureContentHeight]);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!wasOpen && isOpen) {
      const snaps_0 = getEffectiveSnapHeights();
      if (forceExpanded) {
        const lastIndex = snaps_0.length - 1;
        setHeight(snaps_0[lastIndex] ?? minHeight);
        onSnapChange?.(lastIndex);
      } else {
        setHeight(getInitialSnapHeight());
        onSnapChange?.(Math.min(initialSnapIndex, snaps_0.length - 1));
      }
      return;
    }
    if (wasOpen && !isOpen) {
      // NOTE: do NOT call onSnapChange here; snapToNearest already fired it when it
      // triggered setIsOpen(false), and an external close doesn't need a snap callback.
      setHeight(getSnapHeight(0));
      return;
    }
  }, [isOpen, initialSnapIndex, forceExpanded, getEffectiveSnapHeights, getInitialSnapHeight, getSnapHeight, setHeight, onSnapChange, minHeight]);

  // Deferred read so the re-snap picks up pixelSnapHeights from downstream state updates
  // (e.g. useStaticNearbyPins) that land in a later commit than the forceExpanded flip.
  const getInitialSnapHeightRef = useRef(getInitialSnapHeight);
  getInitialSnapHeightRef.current = getInitialSnapHeight;
  const getEffectiveSnapHeightsRef = useRef(getEffectiveSnapHeights);
  getEffectiveSnapHeightsRef.current = getEffectiveSnapHeights;
  useEffect(() => {
    const wasForceExpanded = prevForceExpandedRef.current;
    prevForceExpandedRef.current = forceExpanded;
    if (!isOpen || forceExpanded === wasForceExpanded) return;
    if (forceExpanded) {
      const snaps_1 = getEffectiveSnapHeightsRef.current();
      const lastIndex_0 = snaps_1.length - 1;
      setHeight(snaps_1[lastIndex_0] ?? minHeight);
      onSnapChange?.(lastIndex_0);
    } else if (!fitContent) {
      requestAnimationFrame(() => {
        if (isDraggingRef.current) return;
        const snaps_2 = getEffectiveSnapHeightsRef.current();
        const targetIndex_0 = Math.min(initialSnapIndex, snaps_2.length - 1);
        setHeight(getInitialSnapHeightRef.current());
        onSnapChange?.(targetIndex_0);
      });
    }
  }, [forceExpanded, isOpen, fitContent, initialSnapIndex, setHeight, onSnapChange, minHeight]);
  useEffect(() => {
    if (heightRef.current > currentMaxHeight) {
      setHeight(currentMaxHeight);
    }
  }, [currentMaxHeight, setHeight]);
  const handleDragEnd = useCallback((currentHeight_0: number, velocity_0: number) => {
    isDraggingRef.current = false;
    setHeightState(currentHeight_0);
    snapToNearest(currentHeight_0, velocity_0);
  }, [snapToNearest]);
  const {
    isDragging,
    wasDraggingRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown
  } = useDrawerDrag({
    drawerRef,
    heightRef,
    minHeight,
    maxHeight: getEffectiveMaxHeight(),
    onDragEnd: handleDragEnd
  });
  isDraggingRef.current = isDragging;
  const handleOverlayClick = useCallback(() => {
    setHeight(getSnapHeight(0));
    onSnapChange?.(0);
    setIsOpen(false);
    onOverlayClick?.();
  }, [getSnapHeight, setHeight, setIsOpen, onSnapChange, onOverlayClick]);
  const handleIndicatorClick = useCallback(() => {
    // After a drag gesture on desktop, the click event still fires — skip it.
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    const effectiveSnaps_0 = getEffectiveSnapHeights();
    const lastIndex_1 = effectiveSnaps_0.length - 1;
    const fullHeight = effectiveSnaps_0[lastIndex_1] ?? minHeight;
    if (heightRef.current >= fullHeight - 1) {
      // Already at max — snap down to initial snap point
      const targetIndex_1 = Math.min(initialSnapIndex, lastIndex_1);
      setHeight(getInitialSnapHeight());
      onSnapChange?.(targetIndex_1);
    } else {
      // Not at max — expand to full
      setHeight(fullHeight);
      onSnapChange?.(lastIndex_1);
    }
  }, [wasDraggingRef, getEffectiveSnapHeights, getInitialSnapHeight, setHeight, onSnapChange, initialSnapIndex, minHeight]);
  const customDrawerStyles = {
    ...(borderRadius && {
      borderTopLeftRadius: borderRadius,
      borderTopRightRadius: borderRadius
    }),
    ...(backgroundColor && {
      backgroundColor
    }),
    ...(boxShadow && {
      boxShadow
    }),
    ...(zIndex && {
      zIndex
    }),
    ...(margin && {
      margin
    }),
    ...(maxWidth && {
      maxWidth
    }),
    ...style
  };
  return <>
            {showOverlay &&
    // biome-ignore lint/a11y/noStaticElementInteractions: overlay display
    <div className={`${overlayStyles(isOpen, overlayOpacity)} ${overlayClassName || ''}`} onClick={handleOverlayClick} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleOverlayClick();
      }
    }} />}

            <div className={`${drawerContainerStyles}`}>
                <div ref={drawerRef} className={`${drawerStyles} ${className || ''}`} style={{
        height: `${height}px`,
        transition: isDragging ? 'none' : 'all 0.2s ease',
        ...customDrawerStyles
      }} onTouchStart={e_0 => {
        isTouchActiveRef.current = true;
        handleTouchStart(e_0);
      }} onTouchMove={handleTouchMove} onTouchEnd={e_1 => {
        isTouchActiveRef.current = false;
        handleTouchEnd();
      }}>
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: draggable top area display */}
                    <div className={`${draggableTopAreaStyles} ${draggableAreaClassName || ''}`} onMouseDown={handleMouseDown} onClick={handleIndicatorClick} onKeyDown={e_2 => {
          if (e_2.key === 'Enter' || e_2.key === ' ') {
            handleIndicatorClick();
          }
        }}>
                        <div className={`${swipeIndicatorStyles} ${indicatorClassName || ''}`} style={{
            left: '50%',
            transform: 'translateX(-50%)'
          }} />
                    </div>
                    <div ref={contentRef} className={`${contentStyles} ${contentClassName || ''}`}>
                        {children}
                    </div>
                </div>
            </div>
        </>;
};